import React, { useCallback, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { MMediaImage } from '../MMediaImage';
import { ACarouselArrow, ACarouselArrowDirection } from '../../atoms/ACarouselArrow';
import { MCarouselControls, MCarouselControlsType } from '../MCarouselControls';
import type { MMediaCarouselProps, MMediaCarouselMediaImageItem, MMediaImageMultiItem } from './MMediaCarousel.types';
import { MMediaCarouselCarouselStyle as CarouselStyle } from './MMediaCarousel.types';
import * as Styled from './MMediaCarousel.styles';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const COMPONENT_NAME = 'm-web-ui-kit-MMediaCarousel';
const TEST_ID = 'web-ui-kit-MMediaCarousel';

export const MMediaCarousel = ({
  carouselStyle = CarouselStyle.Standard,
  hover = false,
  logo: _logo = false,
  mMediaImage = [],
  mMediaImageMulti = [],
  renditionSpecs,
  aCarouselArrow,
  mCarouselControls,
  className,
  ...rest
}: MMediaCarouselProps): React.ReactElement | null => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const useLegacySlides = mMediaImage.length === 0 && mMediaImageMulti.length > 0;
  const slides: (MMediaCarouselMediaImageItem | MMediaImageMultiItem)[] = useLegacySlides
    ? mMediaImageMulti
    : mMediaImage;
  const totalSlides = slides.length;

  const handleNext = useCallback(() => {
    if (swiperInstance) {
      swiperInstance.slideNext();
    }
  }, [swiperInstance]);

  const handlePrev = useCallback(() => {
    if (swiperInstance) {
      swiperInstance.slidePrev();
    }
  }, [swiperInstance]);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setCurrentSlide(swiper.activeIndex);
  }, []);

  if (totalSlides === 0) {
    return null;
  }

  const showControls = totalSlides > 1;
  const controlsType = mCarouselControls?.type || MCarouselControlsType.Dots;
  const imageOverlay = mCarouselControls?.imageOverlay !== undefined ? mCarouselControls.imageOverlay : true;
  const showInlineOverlayControls = controlsType === MCarouselControlsType.Dots && imageOverlay;
  const showStandaloneArrows = !showInlineOverlayControls;

  return (
    <Styled.Wrapper
      carouselStyle={carouselStyle}
      hover={hover}
      className={className}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      {...rest}
    >
      <Styled.CarouselContainer>
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={0}
          slidesPerView={1}
          onSwiper={setSwiperInstance}
          onSlideChange={handleSlideChange}
          loop={totalSlides > 1}
        >
          {slides.map((item, index) => (
            <SwiperSlide key={index}>
              <Styled.SlideContent>
                {useLegacySlides ? (
                  <MMediaImage item={item as MMediaImageMultiItem} renditionSpecs={renditionSpecs} />
                ) : (
                  <MMediaImage
                    aImage={(item as MMediaCarouselMediaImageItem).aImage}
                    mButtonIcon={(item as MMediaCarouselMediaImageItem).mButtonIcon}
                    aScrimGradient={(item as MMediaCarouselMediaImageItem).aScrimGradient}
                    mTag={(item as MMediaCarouselMediaImageItem).mTag}
                    renditionSpecs={renditionSpecs}
                  />
                )}
              </Styled.SlideContent>
            </SwiperSlide>
          ))}
        </Swiper>

        {showControls && (
          <>
            {showStandaloneArrows && (
              <Styled.ArrowsWrapper>
                <Styled.ArrowLeft>
                  <ACarouselArrow
                    direction={ACarouselArrowDirection.Left}
                    disabled={aCarouselArrow?.disabled || false}
                    transparent={aCarouselArrow?.transparent || false}
                    onClick={handlePrev}
                  />
                </Styled.ArrowLeft>
                <Styled.ArrowRight>
                  <ACarouselArrow
                    direction={ACarouselArrowDirection.Right}
                    disabled={aCarouselArrow?.disabled || false}
                    transparent={aCarouselArrow?.transparent || false}
                    onClick={handleNext}
                  />
                </Styled.ArrowRight>
              </Styled.ArrowsWrapper>
            )}

            <Styled.ControlsWrapper>
              <MCarouselControls
                type={controlsType}
                imageOverlay={imageOverlay}
                totalSlides={totalSlides}
                currentSlide={currentSlide}
                onNext={handleNext}
                onPrev={handlePrev}
                aIcon={
                  mCarouselControls?.aIcon?.icon
                    ? {
                        icon: mCarouselControls.aIcon.icon,
                        ...(mCarouselControls.aIcon.size !== undefined ? { size: mCarouselControls.aIcon.size } : {}),
                      }
                    : undefined
                }
              />
            </Styled.ControlsWrapper>
          </>
        )}
      </Styled.CarouselContainer>
    </Styled.Wrapper>
  );
};

export default MMediaCarousel;
export { MMediaCarouselCarouselStyle } from './MMediaCarousel.types';
export type { MMediaCarouselProps, MMediaImageMultiItem } from './MMediaCarousel.types';
